if (!customElements.get('header-navigation')) customElements.define('header-navigation', class extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.mobile = this.dataset.presentation === 'mobile';
    this.breakpoint = matchMedia('(min-width: 64rem)');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.effects = new Map();
    this.heightEffects = new Map();
    this.roots = [...this.querySelectorAll('[data-nav-root]')];
    this.categories = [...this.querySelectorAll('[data-nav-category]')];
    this.utilities = [...this.querySelectorAll('[data-nav-utility]')];
    this.tree = this.querySelector('.navigation-tree');
    this.pill = this.querySelector('.navigation-pill');
    this.group = this.querySelector('.navigation-roots');
    this.header = this.closest('.site-header');
    this.roots.concat(this.categories, this.utilities).forEach(details => this.state(details, false));
    if (this.mobile) {
      this.disclosure = this.querySelector('[data-mobile-disclosure]');
      this.opener = this.disclosure.querySelector(':scope > summary');
      this.content = this.querySelector('[data-mobile-content]');
      this.home = this.content.parentElement;
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'navigation-dialog';
      this.dialog.setAttribute('aria-label', this.tree.getAttribute('aria-label'));
      this.append(this.dialog);
      this.dialog.append(this.content);
      this.querySelector('[data-mobile-close]').hidden = false;
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeMobile(); }, { signal });
      this.dialog.addEventListener('close', () => { this.opener.setAttribute('aria-expanded', 'false'); }, { signal });
      this.opener.setAttribute('aria-expanded', 'false');
    }
    this.addEventListener('click', event => this.click(event), { signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        if (this.mobile) this.closeMobile();
        else this.closeRoot(true);
      }
      if (this.mobile && this.dialog.open && event.key === 'Tab') {
        const controls = [...this.dialog.querySelectorAll('a[href],button,summary,[tabindex="0"]')].filter(node => !node.disabled && !node.closest('[inert]') && node.getClientRects().length && node.checkVisibility());
        const first = controls[0], last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }, { signal });
    document.addEventListener('click', event => {
      if (!this.contains(event.target)) this.closeRoot(false);
      if (event.target.closest('[data-cart-open],header-corporate-menu summary')) this.dismiss();
    }, { capture: true, signal });
    document.addEventListener('sparklys:navigation-dismiss', () => this.dismiss(), { signal });
    document.addEventListener('focusin', event => {
      if (!this.mobile && !this.contains(event.target)) this.closeRoot(false);
      this.positionPill();
      this.categoryPills?.forEach(pill => this.positionCategoryPill(pill));
    }, { signal });
    this.group.addEventListener('pointerover', event => { const target = event.target.closest('.navigation-trigger'); if (target) { this.hovered = target; this.positionPill(); } }, { signal });
    this.group.addEventListener('pointerleave', () => { this.hovered = null; this.positionPill(); }, { signal });
    this.breakpoint.addEventListener('change', () => {
      const hadFocus = this.contains(document.activeElement);
      this.dismiss();
      if (hadFocus) this.header.querySelector(this.breakpoint.matches ? '.site-header__main > .site-header__inner > .site-header__logo' : '.navigation-mobile-opener')?.focus();
      this.measure();
    }, { signal });
    this.reduced.addEventListener('change', () => {
      this.effects.forEach(animation => animation.finish());
      this.heightEffects.forEach(animation => animation.finish());
      this.positionPill();
    }, { signal });
    window.addEventListener('scroll', () => this.measure(), { passive: true, signal });
    this.resize = new ResizeObserver(() => { this.measure(); this.updateScrollers(); });
    this.resize.observe(this.header);
    this.resize.observe(this.group);
    this.panelHeights = new WeakMap();
    this.panelResize = new ResizeObserver(entries => {
      for (const { target } of entries) {
        const panel = target.closest('.navigation-panel');
        if (this.mobile || panel.closest('[data-nav-root]') !== this.root) continue;
        this.easePanelHeight(panel, this.panelHeights.get(panel));
      }
    });
    this.querySelectorAll('.navigation-categories, .navigation-panel > .navigation-content').forEach(body => this.panelResize.observe(body));
    this.querySelectorAll('.navigation-cards').forEach(rail => {
      rail.addEventListener('scroll', () => this.updateScrollers(), { passive: true, signal });
      this.resize.observe(rail);
    });
    this.categoryPills = [];
    if (!this.mobile) this.querySelectorAll('.navigation-categories').forEach(group => {
      const pill = document.createElement('div');
      pill.className = 'navigation-category-pill';
      pill.setAttribute('aria-hidden', 'true');
      pill.hidden = true;
      group.append(pill);
      this.categoryPills.push(pill);
      group.addEventListener('pointerover', event => {
        const target = event.target.closest('[data-nav-category] > summary, .navigation-category-link');
        if (target) { pill.hovered = target; this.positionCategoryPill(pill); }
        else if (event.target.closest('.navigation-content')) { pill.hovered = null; this.positionCategoryPill(pill); }
      }, { signal });
      group.addEventListener('pointerleave', () => { pill.hovered = null; this.positionCategoryPill(pill); }, { signal });
    });
    this.dataset.enhanced = '';
    this.initializingPill = true;
    this.measure();
    this.initializingPill = false;
    document.fonts.ready.then(() => { if (this.isConnected) this.measure(); });
  }
  state(details, open) {
    details.open = open;
    details.querySelector(':scope > summary').setAttribute('aria-expanded', String(open));
    const panel = details.querySelector(':scope > div');
    if (panel) panel.inert = !open;
  }
  animate(node, frames, kind = 'base', done, timing = {}) {
    this.effects.get(node)?.cancel();
    if (this.reduced.matches) { done?.(); return; }
    const styles = getComputedStyle(this);
    const duration = parseFloat(styles.getPropertyValue(`--motion-duration-${kind}`)) || 260;
    const animation = node.animate(frames, { duration, easing: styles.getPropertyValue('--motion-ease').trim() || 'ease-out', fill: 'both', ...timing });
    this.effects.set(node, animation);
    animation.finished.then(() => {
      if (this.effects.get(node) !== animation) return;
      this.effects.delete(node);
      animation.cancel();
      done?.();
    }).catch(() => {});
  }
  transition(details, open, immediate = false, slide = 0) {
    const panel = details.querySelector(':scope > div');
    const previous = getComputedStyle(panel);
    const opacity = details.open ? previous.opacity : '0';
    const height = details.open ? panel.getBoundingClientRect().height : 0;
    const paddingStart = details.open ? previous.paddingBlockStart : '0px';
    const paddingEnd = details.open ? previous.paddingBlockEnd : '0px';
    const transform = details.open ? previous.transform : 'translateY(-6px)';
    const accordion = this.mobile && details.matches('[data-nav-category], [data-nav-utility]');
    const slidingRoot = this.mobile && details.matches('[data-nav-root]') && slide !== 0;
    this.effects.get(panel)?.cancel(); this.effects.delete(panel);
    details.querySelector(':scope > summary').setAttribute('aria-expanded', String(open));
    panel.inert = !open;
    if (immediate || this.reduced.matches) { this.state(details, open); return; }
    details.open = true;
    const desktopCategory = !this.mobile && details.hasAttribute('data-nav-category');
    // Cards own desktop category entry; do not multiply their fade by a panel fade.
    if (desktopCategory && open) return;
    const frames = accordion
      ? [{ height: `${height}px`, paddingBlockStart: paddingStart, paddingBlockEnd: paddingEnd, opacity, transform, overflow: 'hidden' }, { height: `${open ? panel.scrollHeight : 0}px`, paddingBlockStart: open ? getComputedStyle(panel).paddingBlockStart : '0px', paddingBlockEnd: open ? getComputedStyle(panel).paddingBlockEnd : '0px', opacity: open ? 1 : 0, transform: open ? 'translateY(0)' : 'translateY(-6px)', overflow: 'hidden' }]
      : slidingRoot
        ? [{ opacity: open ? 1 : opacity, transform: open ? `translateX(${slide * 14}px)` : transform }, { opacity: open ? 1 : 0, transform: open ? 'translateX(0)' : `translateX(${-slide * 14}px)` }]
      : desktopCategory ? [{ opacity }, { opacity: 0 }]
      : [{ opacity, transform }, { opacity: open ? 1 : 0, transform: `translateY(${open ? 0 : -6}px)` }];
    const timing = desktopCategory
      ? { duration: parseFloat(getComputedStyle(this).getPropertyValue('--motion-duration-navigation-content')) / 2, easing: getComputedStyle(this).getPropertyValue('--motion-ease-ui').trim() }
      : accordion || slidingRoot ? { easing: getComputedStyle(this).getPropertyValue('--motion-ease-ui').trim() } : {};
    this.animate(panel, frames, desktopCategory ? 'navigation-content' : accordion || slidingRoot ? 'base' : 'slow', () => { if (!open) { details.open = false; if (details.hasAttribute('data-nav-category')) this.clearLeaving(panel); } }, timing);
  }
  selectRoot(root) {
    if (this.root === root) { if (!this.mobile) this.closeRoot(false); return; }
    const direction = this.mobile && this.root ? Math.sign(this.roots.indexOf(root) - this.roots.indexOf(this.root)) : 0;
    const mobileHeight = this.mobile && direction ? this.group.getBoundingClientRect().height : 0;
    if (this.mobile) this.clearLeaving(root.querySelector(':scope > .navigation-panel'));
    if (this.root) {
      if (this.mobile) {
        const outgoing = this.root.querySelector(':scope > .navigation-panel');
        const rect = outgoing.getBoundingClientRect(), origin = this.group.getBoundingClientRect();
        Object.assign(outgoing.style, { position: 'absolute', left: '0', top: `${rect.top - origin.top}px`, width: '100%' });
      }
      this.transition(this.root, false, false, direction);
    }
    this.root = root;
    this.header.querySelectorAll('[data-corporate-menu][open]').forEach(details => { details.open = false; });
    const categories = [...root.querySelectorAll('[data-nav-category]')];
    categories.forEach(details => this.clearLeaving(details.querySelector(':scope > .navigation-content')));
    categories.forEach((details, index) => this.transition(details, index === 0, true));
    root.selectedCategory = categories[0] || null;
    this.transition(root, true, false, direction);
    if (direction) this.easeMobileHeight(this.group, mobileHeight);
    if (!direction) this.reveal(root.selectedCategory || root);
    this.updateScrollers();
    this.positionPill();
  }
  closeRoot(restore = false, immediate = false) {
    if (!this.root) return;
    const root = this.root;
    this.root = null;
    this.transition(root, false, immediate);
    if (restore) root.querySelector('summary').focus({ preventScroll: true });
    this.positionPill();
  }
  easePanelHeight(panel, fromHeight) {
    if (this.mobile || !panel.closest('[data-nav-root]').open) return;
    const running = this.heightEffects.get(panel);
    const paintedHeight = running ? panel.getBoundingClientRect().height : fromHeight;
    // Observe intrinsic content, never feed an interpolated panel height back into the target.
    const body = panel.querySelector(':scope > .navigation-categories, :scope > .navigation-content');
    const style = getComputedStyle(panel);
    const naturalHeight = body.getBoundingClientRect().height + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const maximum = parseFloat(style.maxHeight);
    const toHeight = Number.isFinite(maximum) ? Math.min(naturalHeight, maximum) : naturalHeight;
    if (Math.abs((this.panelHeights.get(panel) ?? 0) - toHeight) < 1) return;
    this.panelHeights.set(panel, toHeight);
    running?.cancel();
    this.heightEffects.delete(panel);
    if (this.reduced.matches || !paintedHeight || Math.abs(paintedHeight - toHeight) < 1) return;
    const tokens = getComputedStyle(this);
    const animation = panel.animate([{ height: `${paintedHeight}px` }, { height: `${toHeight}px` }], {
      duration: parseFloat(tokens.getPropertyValue('--motion-duration-navigation-content')),
      easing: tokens.getPropertyValue('--motion-ease-ui').trim(),
    });
    this.heightEffects.set(panel, animation);
    animation.finished.then(() => {
      if (this.heightEffects.get(panel) === animation) this.heightEffects.delete(panel);
    }).catch(() => {});
  }
  easeMobileHeight(element, fromHeight) {
    const running = this.heightEffects.get(element);
    const targetHeight = element.getBoundingClientRect().height;
    running?.cancel();
    this.heightEffects.delete(element);
    if (this.reduced.matches || !fromHeight || Math.abs(fromHeight - targetHeight) < 1) return;
    const styles = getComputedStyle(this);
    const animation = element.animate([{ height: `${fromHeight}px` }, { height: `${targetHeight}px` }], {
      duration: parseFloat(styles.getPropertyValue('--motion-duration-base')),
      easing: styles.getPropertyValue('--motion-ease-ui').trim(),
    });
    this.heightEffects.set(element, animation);
    animation.finished.then(() => {
      if (this.heightEffects.get(element) === animation) this.heightEffects.delete(element);
    }).catch(() => {});
  }
  clearLeaving(content) {
    for (const property of ['position', 'left', 'top', 'width', 'grid-column']) content.style.removeProperty(property);
  }
  selectCategory(category) {
    const root = category.closest('[data-nav-root]');
    const current = root.selectedCategory;
    if (current === category && !this.mobile) return;
    const panel = root.querySelector(':scope > .navigation-panel');
    const fromHeight = panel.getBoundingClientRect().height;
    const incoming = category.querySelector(':scope > .navigation-content');
    const paintedOpacity = category.open ? Number(getComputedStyle(incoming).opacity) : 0;
    if (!this.mobile) {
      // Only the incoming category determines grid height. The outgoing content fades
      // at its painted position without holding the grid open until its fade completes.
      this.clearLeaving(incoming);
      if (current) {
        const outgoing = current.querySelector(':scope > .navigation-content');
        const rect = outgoing.getBoundingClientRect(), origin = outgoing.parentElement.parentElement.getBoundingClientRect();
        Object.assign(outgoing.style, { position: 'absolute', gridColumn: '1 / -1', left: `${rect.left - origin.left}px`, top: `${rect.top - origin.top}px`, width: `${rect.width}px` });
      }
    }
    if (current) this.transition(current, false);
    root.selectedCategory = current === category ? null : category;
    if (root.selectedCategory) { this.transition(category, true); this.reveal(category, !this.mobile, paintedOpacity); }
    this.updateScrollers();
    this.easePanelHeight(panel, fromHeight);
  }
  reveal(owner, categoryChange = false, paintedOpacity = 0) {
    if (this.reduced.matches) return;
    const cards = [...owner.querySelectorAll('.navigation-cards > *')].filter(card => !card.closest('[inert]'));
    const phaseDuration = parseFloat(getComputedStyle(this).getPropertyValue('--motion-duration-navigation-content')) / 2;
    if (categoryChange) {
      const heading = owner.querySelector('.navigation-content__heading');
      this.animate(heading, [{ opacity: Number(getComputedStyle(heading).opacity) * paintedOpacity }, { opacity: 1 }], 'navigation-content', undefined, { duration: phaseDuration, delay: paintedOpacity > 0 ? 0 : phaseDuration, easing: getComputedStyle(this).getPropertyValue('--motion-ease-ui').trim() });
    }
    cards.forEach((card, index) => {
      if (categoryChange) {
        // Real delays preserve the full fade duration for every card. On reversal,
        // continue from the combined painted card/panel opacity without waiting.
        const opacity = Number(getComputedStyle(card).opacity) * paintedOpacity;
        this.animate(card, [{ opacity }, { opacity: 1 }], 'navigation-content', undefined, {
          duration: phaseDuration,
          delay: opacity > 0 ? 0 : phaseDuration + Math.min(index * 40, 120),
          easing: getComputedStyle(this).getPropertyValue('--motion-ease-ui').trim(),
        });
      } else {
        const delay = Math.min(index * 40, 160);
        this.animate(card, [{ opacity: 0, offset: 0 }, { opacity: 0, offset: delay / (360 + delay) }, { opacity: 1, offset: 1 }], 'slow');
      }
    });
  }
  click(event) {
    const summary = event.target.closest('summary');
    if (summary === this.opener) { event.preventDefault(); this.openMobile(); return; }
    if (event.target.closest('[data-mobile-close]')) { this.closeMobile(); return; }
    if (summary?.parentElement.matches('[data-nav-root]')) { event.preventDefault(); this.selectRoot(summary.parentElement); return; }
    if (summary?.parentElement.matches('[data-nav-category]')) { event.preventDefault(); this.selectCategory(summary.parentElement); return; }
    if (summary?.parentElement.matches('[data-nav-utility]')) {
      event.preventDefault();
      const selected = summary.parentElement;
      const open = summary.getAttribute('aria-expanded') !== 'true';
      this.utilities.forEach(details => {
        if (details === selected || details.querySelector('summary').getAttribute('aria-expanded') === 'true') this.transition(details, details === selected && open);
      });
      return;
    }
    const button = event.target.closest('[data-scroll]');
    if (button) {
      const rail = button.closest('.navigation-content').querySelector('.navigation-cards');
      rail.scrollBy({ left: Number(button.dataset.scroll) * rail.clientWidth * .85, behavior: this.reduced.matches ? 'instant' : 'smooth' });
    }
    if (event.target.closest('a[href]')) this.dismiss();
  }
  openMobile() {
    if (!this.dialog || this.dialog.open) return;
    this.header.querySelectorAll('header-navigation').forEach(nav => { if (nav !== this) nav.dismiss(); });
    this.header.querySelectorAll('[data-corporate-menu][open]').forEach(details => { details.open = false; });
    document.documentElement.style.setProperty('--navigation-scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
    this.dialog.style.width = `${this.header.querySelector('.site-header__main').getBoundingClientRect().width}px`;
    this.dialog.showModal();
    getComputedStyle(this.dialog, '::backdrop').opacity;
    this.dialog.removeAttribute('data-closing');
    this.dialog.setAttribute('data-backdrop-visible', '');
    // Establish the closed glyph before transitioning both aligned header copies.
    getComputedStyle(this.querySelector('[data-mobile-close] .mobile-menu__icon'), '::before').transform;
    this.setAttribute('data-menu-open', '');
    this.opener.setAttribute('aria-expanded', 'true');
    if (this.roots[0]) this.selectRoot(this.roots[0]);
    this.animate(this.content, [{ opacity: 1, transform: 'translateY(0.75rem)' }, { opacity: 1, transform: 'translateY(0)' }], 'slow', undefined, { easing: getComputedStyle(this).getPropertyValue('--motion-ease-ui').trim() });
    this.querySelector('[data-mobile-close]').focus();
  }
  closeMobile(immediate = false, restore = true) {
    if (!this.dialog?.open) return;
    this.removeAttribute('data-menu-open');
    this.opener.setAttribute('aria-expanded', 'false');
    const contentStyle = getComputedStyle(this.content);
    this.dialog.setAttribute('data-closing', '');
    this.dialog.removeAttribute('data-backdrop-visible');
    const finish = () => { this.dialog.close(); this.closeRoot(false, true); if (restore && this.isConnected) this.opener.focus({ preventScroll: true }); };
    if (immediate) { this.effects.get(this.content)?.cancel(); finish(); }
    else this.animate(this.content, [{ opacity: Number(contentStyle.opacity), transform: contentStyle.transform }, { opacity: 0, transform: 'translateY(0.75rem)' }], 'slow', finish, { easing: getComputedStyle(this).getPropertyValue('--motion-ease-ui').trim() });
  }
  dismiss() { this.closeRoot(false, true); this.closeMobile(true, false); }
  updateScrollers() {
    this.querySelectorAll('.navigation-content').forEach(content => {
      const rail = content.querySelector('.navigation-cards');
      const previous = content.querySelector('[data-scroll="-1"]');
      const next = content.querySelector('[data-scroll="1"]');
      const controls = content.querySelector('.navigation-controls');
      if (controls) controls.hidden = rail.clientWidth === 0 || rail.scrollWidth <= rail.clientWidth + 2;
      if (previous) previous.disabled = rail.scrollLeft <= 1;
      if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
    });
  }
  measure() {
    if (!this.header) return;
    if (this.dialog?.open) this.dialog.style.width = `${this.header.querySelector('.site-header__main').getBoundingClientRect().width}px`;
    // Overlay panels do not contribute to the existing sticky-header height observer.
    this.style.setProperty('--navigation-top', `${this.header.querySelector('.site-header__main').getBoundingClientRect().bottom}px`);
    this.positionPill();
    this.categoryPills?.forEach(pill => this.positionCategoryPill(pill));
  }
  positionCategoryPill(pill) {
    const group = pill.parentElement;
    const focused = group.contains(document.activeElement) && document.activeElement.matches("summary:focus-visible, .navigation-category-link:focus-visible") ? document.activeElement : null;
    const target = focused || pill.hovered;
    clearTimeout(pill.hideTimer);
    if (!target || !this.breakpoint.matches || !group.checkVisibility()) {
      if (!pill.hidden) pill.hideTimer = setTimeout(() => {
        const painted = getComputedStyle(pill);
        const opacity = painted.opacity;
        Object.assign(pill.style, { transform: painted.transform, width: painted.width, height: painted.height });
        pill.target = null;
        this.animate(pill, [{ opacity }, { opacity: 0 }], 'fast', () => { pill.hidden = true; });
      }, this.reduced.matches ? 0 : 140);
      return;
    }
    const origin = group.getBoundingClientRect(), rect = target.getBoundingClientRect();
    const previous = pill.hidden ? rect : pill.getBoundingClientRect();
    const opacity = pill.hidden ? 0 : getComputedStyle(pill).opacity;
    const frame = box => ({ transform: `translate(${box.left - origin.left}px, ${box.top - origin.top}px)`, width: `${box.width}px`, height: `${box.height}px` });
    const end = frame(rect), key = JSON.stringify(end);
    if (pill.target === target && pill.end === key) return;
    pill.hidden = false; pill.target = target; pill.end = key;
    Object.assign(pill.style, end, { opacity: "1" });
    this.animate(pill, [{ ...frame(previous), opacity }, { ...end, opacity: 1 }], "navigation");
  }
  positionPill() {
    if (this.mobile || !this.pill || !this.breakpoint.matches) return;
    const focused = this.contains(document.activeElement) && document.activeElement.matches('.navigation-trigger:focus-visible') ? document.activeElement : null;
    const target = focused || this.hovered || this.querySelector('.navigation-trigger[data-current]');
    clearTimeout(this.pillHideTimer);
    if (!target) {
      if (this.pill.hidden) return;
      this.pillHideTimer = setTimeout(() => {
        const painted = getComputedStyle(this.pill);
        const opacity = painted.opacity;
        Object.assign(this.pill.style, { transform: painted.transform, width: painted.width, height: painted.height });
        this.animate(this.pill, [{ opacity }, { opacity: 0 }], 'fast', () => {
          this.pill.hidden = true;
          this.pillTarget = null;
        });
      }, this.reduced.matches ? 0 : 140);
      return;
    }
    const origin = this.header.querySelector('.site-header__inner').getBoundingClientRect(), rect = target.getBoundingClientRect();
    const wasHidden = this.pill.hidden;
    const previous = wasHidden ? rect : this.pill.getBoundingClientRect();
    const opacity = wasHidden ? 0 : getComputedStyle(this.pill).opacity;
    this.pill.hidden = false;
    const frame = box => ({ transform: `translate(${box.left - origin.left}px, ${box.top - origin.top}px)`, width: `${box.width}px`, height: `${box.height}px` });
    // Snap the settled bounds, keeping the interrupted frame untouched for smooth reversal.
    const pixel = value => Math.round(value * window.devicePixelRatio) / window.devicePixelRatio;
    const end = { transform: `translate(${pixel(rect.left - origin.left)}px, ${pixel(rect.top - origin.top)}px)`, width: `${pixel(rect.width)}px`, height: `${pixel(rect.height)}px` };
    const key = JSON.stringify(end);
    if (this.pillTarget === target && this.pillEnd === key && opacity === '1') return;
    this.pillTarget = target; this.pillEnd = key;
    Object.assign(this.pill.style, end, { opacity: '1' });
    // The server already paints the current item. Hand it to the pill without replaying entry.
    if (!this.initializingPill) this.animate(this.pill, [{ ...frame(previous), opacity }, { ...end, opacity: 1 }], 'navigation');
  }

  disconnectedCallback() {
    clearTimeout(this.pillHideTimer);
    this.categoryPills?.forEach(pill => { clearTimeout(pill.hideTimer); pill.remove(); });
    this.dismiss();
    this.effects?.forEach(animation => animation.cancel());
    this.heightEffects?.forEach(animation => animation.cancel());
    this.panelResize?.disconnect();
    this.resize?.disconnect();
    this.abort?.abort(); this.abort = null;
    if (this.dialog) { this.home.append(this.content); this.dialog.remove(); }
    delete this.dataset.enhanced;
  }
});
